import React from 'react';
import { Award, GraduationCap, Star, Check } from 'lucide-react';
import { Button } from './ui/button';
import { doctorInfo } from '../data/mock';
import { useAppointment } from '../context/AppointmentContext';

// Dr. Patterson data sourced from MeetTheDoctor.jsx
const pattersonInfo = {
  name: "Dr. Pickens Patterson, III",
  title: "Pain Management & Anesthesiology Specialist",
  credentials: ["Board Certified in Anesthesiology", "Board Certified in Pain Management"],
  bio: "A native born Georgian, who grew up in Southwest Atlanta, Dr. Patterson takes pride in serving his community and the people of Georgia. Dr. Patterson has over 15 years of experience practicing pain management and anesthesiology. Additionally, Dr. Patterson is trained in advanced pain techniques such as spinal cord stimulation and implantable devices.",
  image: "/webp-images/Dr-Pickens-Patterson.webp",
  education: [
    "Medical Degree from the Medical College of Georgia",
    "Residency in Anesthesiology and Fellowship in Pain Management at Vanderbilt University"
  ],
  accolades: [
    "Medical Association of Georgia member",
    "Georgia Society of Interventional Pain Management member",
    "Selected to be a part of the development of physician leaders in the state of Georgia sponsored by the Medical Association of Georgia"
  ],
  experience: "15+ Years"
};

const DoctorSection = () => {
  const { openAppointmentModal } = useAppointment();

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">

        {/* ── Dr. Carroll D. Cooper ── */}
        <div className="grid lg:grid-cols-2 gap-16 items-start mb-32">
          {/* Photo */}
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={doctorInfo.image}
                alt={doctorInfo.name}
                className="w-full h-[650px] object-cover object-[center_15%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B3A]/40 via-transparent to-transparent"></div>
            </div>
            <div className="absolute -bottom-8 -right-8 bg-white rounded-2xl shadow-2xl p-8 border-t-4 border-[#E63946]">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-br from-[#E63946] to-[#D32F3D] p-4 rounded-xl">
                  <Star className="h-10 w-10 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">{doctorInfo.experience}</div>
                  <div className="text-sm text-gray-600 font-medium">Experience</div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-[#20B2AA]/10 text-[#1B1B3A] px-5 py-2 rounded-full text-sm font-semibold mb-6">
              <GraduationCap className="h-4 w-4" />
              <span>Meet Our Founder & Medical Director</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{doctorInfo.name}</h2>
            <p className="text-xl text-gray-600 mb-6">{doctorInfo.title}</p>
            <div className="flex flex-wrap gap-3 mb-8">
              {doctorInfo.credentials.map((cred, idx) => (
                <span key={idx} className="bg-gradient-to-r from-[#1B1B3A] to-[#2D2D4A] text-white px-4 py-2 rounded-lg font-semibold text-sm">{cred}</span>
              ))}
            </div>
            <div className="bg-red-50 border-l-4 border-[#E63946] p-8 mb-8 rounded-r-xl">
              <p className="text-lg text-gray-700 leading-relaxed">{doctorInfo.bio}</p>
            </div>
            <div className="space-y-4 mb-8">
              <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                <Award className="h-5 w-5 text-[#20B2AA]" />
                Education & Training:
              </h3>
              {doctorInfo.education.map((edu, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{edu}</span>
                </div>
              ))}
            </div>
            <div className="space-y-3 mb-8 bg-gradient-to-br from-red-50 to-white p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 text-lg">Accolades:</h3>
              {doctorInfo.accolades.map((accolade, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-[#E63946] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm">{accolade}</span>
                </div>
              ))}
            </div>
            <Button
              size="lg"
              className="bg-[#E63946] hover:bg-[#D32F3D] text-white px-8 py-6 text-lg font-semibold"
              onClick={openAppointmentModal}
              data-testid="doctor-schedule-consultation-btn"
            >
              Schedule Consultation with Dr. Cooper
            </Button>
          </div>
        </div>

        {/* ── Dr. Pickens Patterson, III ── */}
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Content — left side this time */}
          <div className="order-1 lg:order-1">
            <div className="inline-flex items-center gap-2 bg-[#20B2AA]/10 text-[#1B1B3A] px-5 py-2 rounded-full text-sm font-semibold mb-6">
              <GraduationCap className="h-4 w-4" />
              <span>Pain Management Specialist</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{pattersonInfo.name}</h2>
            <p className="text-xl text-gray-600 mb-6">{pattersonInfo.title}</p>
            <div className="flex flex-wrap gap-3 mb-8">
              {pattersonInfo.credentials.map((cred, idx) => (
                <span key={idx} className="bg-gradient-to-r from-[#1B1B3A] to-[#2D2D4A] text-white px-4 py-2 rounded-lg font-semibold text-sm">{cred}</span>
              ))}
            </div>
            <div className="bg-red-50 border-l-4 border-[#E63946] p-8 mb-8 rounded-r-xl">
              <p className="text-lg text-gray-700 leading-relaxed">{pattersonInfo.bio}</p>
            </div>
            <div className="space-y-4 mb-8">
              <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                <Award className="h-5 w-5 text-[#20B2AA]" />
                Education & Training:
              </h3>
              {pattersonInfo.education.map((edu, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{edu}</span>
                </div>
              ))}
            </div>
            <div className="space-y-3 mb-8 bg-gradient-to-br from-red-50 to-white p-6 rounded-xl">
              <h3 className="font-bold text-gray-900 text-lg">Accolades:</h3>
              {pattersonInfo.accolades.map((accolade, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-[#E63946] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm">{accolade}</span>
                </div>
              ))}
            </div>
            <Button
              size="lg"
              className="bg-[#E63946] hover:bg-[#D32F3D] text-white px-8 py-6 text-lg font-semibold"
              onClick={openAppointmentModal}
            >
              Schedule Consultation with Dr. Patterson
            </Button>
          </div>

          {/* Photo — right side */}
          <div className="relative order-2 lg:order-2">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={pattersonInfo.image}
                alt={pattersonInfo.name}
                className="w-full h-[650px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B1B3A]/40 via-transparent to-transparent"></div>
            </div>
            <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-2xl p-8 border-t-4 border-[#E63946]">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-br from-[#E63946] to-[#D32F3D] p-4 rounded-xl">
                  <Star className="h-10 w-10 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900">{pattersonInfo.experience}</div>
                  <div className="text-sm text-gray-600 font-medium">Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default DoctorSection;